import { Alert, Box, Button, CircularProgress, Fade, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { AreaSelector } from './components/AreaSelector';
import { AttachmentsSection } from './components/AttachmentsSection';
import { DiagnosticsNotice } from './components/DiagnosticsNotice';
import { ExtraDetailsSection } from './components/ExtraDetailsSection';
import { LauncherButton } from './components/LauncherButton';
import { SeveritySelector } from './components/SeveritySelector';
import { SuccessState } from './components/SuccessState';
import { UserDetailsFields } from './components/UserDetailsFields';
import { WidgetHeader } from './components/WidgetHeader';
import { useQAForm } from './hooks/useQAForm';
import { useScreenshot } from './hooks/useScreenshot';
import { submitQAReport } from './services/qaSubmitService';
import { initTelemetry } from './telemetry';
import { type QAFeedbackWidgetProps, type WidgetStatus } from './types';

const QAFeedbackWidget: React.FC<QAFeedbackWidgetProps> = ({ qaApiKey }) => {
  const apiKey = qaApiKey || (import.meta.env.VITE_QA_API_KEY as string);
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<WidgetStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const {
    formData,
    setTitle,
    setDescription,
    setSeverity,
    setArea,
    setSubArea,
    setStepsToReproduce,
    setExpectedResult,
    setActualResult,
    setReporterName,
    setReporterEmail,
    resetForm,
  } = useQAForm(isOpen);

  const {
    autoScreenshot,
    autoScreenshotUrl,
    isCapturing,
    extraFiles,
    captureScreen,
    removeAutoScreenshot,
    addExtraFiles,
    removeExtraFile,
    clearAttachments,
  } = useScreenshot();

  // Initialize client telemetry buffers once on mount
  useEffect(() => {
    initTelemetry();
  }, []);

  const handleOpenWidget = async () => {
    setIsOpen(true);
    setStatus('idle');
    setErrorMessage('');

    if (!autoScreenshot) {
      await captureScreen();
    }
  };

  const handleCloseWidget = () => {
    if (status === 'submitting') return;
    setIsOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) return;

    setStatus('submitting');
    setErrorMessage('');

    try {
      await submitQAReport({
        apiKey,
        formData,
        autoScreenshot,
        extraFiles,
      });

      setStatus('success');

      setTimeout(() => {
        setIsOpen(false);
        setStatus('idle');
        resetForm();
        clearAttachments();
      }, 2200);
    } catch (err) {
      console.error('QAWidget submission error:', err);
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Failed to submit bug report. Please try again.');
    }
  };

  return (
    <>
      <LauncherButton isOpen={isOpen} onToggle={isOpen ? handleCloseWidget : handleOpenWidget} />

      <Fade in={isOpen}>
        <Box
          data-no-invert="true"
          role="dialog"
          aria-modal="true"
          aria-labelledby="qa-panel-title"
          sx={{
            position: 'fixed',
            bottom: { xs: 154, sm: 148 },
            right: { xs: 12, sm: 24 },
            zIndex: 9999,
            width: 'min(92vw, 380px)',
            maxHeight: 'min(78vh, 620px)',
            display: isOpen ? 'flex' : 'none',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
            color: '#1f2937',
            borderRadius: '16px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
          }}
        >
          <WidgetHeader onClose={handleCloseWidget} disabled={status === 'submitting'} />

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: 2,
              overflowY: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.75,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 3 },
            }}
          >
            {status === 'success' ? (
              <SuccessState />
            ) : (
              <>
                <Typography variant="caption" sx={{ color: '#6b7280', lineHeight: 1.4 }}>
                  Found something broken? Tell us what happened — we'll take a look.
                </Typography>

                {status === 'error' && (
                  <Alert severity="error" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                    {errorMessage}
                  </Alert>
                )}

                {/* Title */}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                    Title <span style={{ color: '#ef4444' }}>*</span>
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder='e.g. "Save button does nothing"'
                    value={formData.title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    disabled={status === 'submitting'}
                    inputProps={{ style: { fontSize: '0.85rem' } }}
                  />
                </Box>

                {/* What happened? */}
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 0.5 }}>
                    What happened? <span style={{ color: '#ef4444' }}>*</span>
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Describe the problem…"
                    value={formData.description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    disabled={status === 'submitting'}
                    inputProps={{ style: { fontSize: '0.85rem' } }}
                  />
                </Box>

                <SeveritySelector value={formData.severity} onChange={setSeverity} disabled={status === 'submitting'} />

                <AreaSelector
                  area={formData.area}
                  subArea={formData.subArea}
                  onAreaChange={setArea}
                  onSubAreaChange={setSubArea}
                  disabled={status === 'submitting'}
                />

                <ExtraDetailsSection
                  stepsToReproduce={formData.stepsToReproduce}
                  expectedResult={formData.expectedResult}
                  actualResult={formData.actualResult}
                  onStepsChange={setStepsToReproduce}
                  onExpectedChange={setExpectedResult}
                  onActualChange={setActualResult}
                  disabled={status === 'submitting'}
                />

                <AttachmentsSection
                  autoScreenshotUrl={autoScreenshotUrl}
                  isCapturing={isCapturing}
                  extraFiles={extraFiles}
                  onRemoveAutoScreenshot={removeAutoScreenshot}
                  onAddFiles={addExtraFiles}
                  onRemoveExtraFile={removeExtraFile}
                  disabled={status === 'submitting'}
                />

                <UserDetailsFields
                  reporterName={formData.reporterName}
                  reporterEmail={formData.reporterEmail}
                  onNameChange={setReporterName}
                  onEmailChange={setReporterEmail}
                  disabled={status === 'submitting'}
                />

                <DiagnosticsNotice />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={!formData.title.trim() || !formData.description.trim() || status === 'submitting'}
                  sx={{
                    mt: 0.5,
                    height: 40,
                    backgroundColor: '#0F9A00',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    textTransform: 'none',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#0d8300',
                      boxShadow: 'none',
                    },
                    '&:disabled': {
                      backgroundColor: '#e5e7eb',
                      color: '#9ca3af',
                    },
                  }}
                >
                  {status === 'submitting' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} sx={{ color: '#ffffff' }} />
                      <span>Submitting report…</span>
                    </Box>
                  ) : (
                    'Submit report'
                  )}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Fade>
    </>
  );
};

export default QAFeedbackWidget;
